import * as THREE from 'three';
import { buildDuckModel } from '../entities/Duck';
import { SKINS, skinById, type Skin } from '../entities/DuckSkins';
import type { Profile } from '../game/Profile';
import { disposeObject3D } from '../utils/dispose';

const THUMB = 128;

/**
 * The duck shop on the title screen: a turntable preview of the selected
 * duck, a grid of every duck, and buy/equip. Uses its own small renderer and
 * only draws while open.
 */
export class Shop {
  private readonly root = this.get('#shop');
  private readonly canvas = this.get('#shop-preview') as HTMLCanvasElement;
  private readonly grid = this.get('#shop-grid');
  private readonly nameLabel = this.get('#shop-name');
  private readonly action = this.get('#shop-action') as HTMLButtonElement;
  private readonly coinsLabel = this.get('#shop-coins');
  private renderer: THREE.WebGLRenderer | null = null;
  private readonly scene = new THREE.Scene();
  private readonly camera = new THREE.PerspectiveCamera(30, 1, 0.1, 20);
  private model: THREE.Group | null = null;
  private selected: Skin;
  private frame = 0;
  private thumbs = new Map<string, string>();

  constructor(
    private readonly profile: Profile,
    private readonly onEquip: (skin: Skin) => void,
    private readonly onClose: () => void,
  ) {
    this.selected = profile.skin;
    this.scene.add(new THREE.HemisphereLight('#ffffff', '#5a7d6a', 1.6));
    const sun = new THREE.DirectionalLight('#fff4dc', 2.2);
    sun.position.set(2, 4, 3);
    this.scene.add(sun);
    this.camera.position.set(1.7, 1.5, -2.6);
    this.camera.lookAt(0, 0.5, 0);
    this.action.addEventListener('click', this.onAction);
    this.get('#shop-close').addEventListener('click', () => this.close());
    // Taps inside the shop must not reach the title screen (which starts a race).
    this.root.addEventListener('pointerdown', (event) => event.stopPropagation());
  }

  get isOpen(): boolean {
    return !this.root.hidden;
  }

  open(): void {
    this.root.hidden = false;
    this.selected = this.profile.skin;
    if (!this.renderer) {
      this.renderer = new THREE.WebGLRenderer({ canvas: this.canvas, antialias: true, alpha: true });
      this.renderer.outputColorSpace = THREE.SRGBColorSpace;
      this.renderer.setPixelRatio(Math.min(2, window.devicePixelRatio));
      this.makeThumbs();
    }
    this.buildGrid();
    this.show(this.selected);
    const loop = (t: number) => {
      if (!this.isOpen) return;
      this.draw(t / 1000);
      this.frame = requestAnimationFrame(loop);
    };
    this.frame = requestAnimationFrame(loop);
  }

  close(): void {
    this.root.hidden = true;
    cancelAnimationFrame(this.frame);
    this.onClose();
  }

  private readonly onAction = () => {
    const skin = this.selected;
    if (this.profile.owned.has(skin.id)) this.profile.equip(skin.id);
    else if (!this.profile.buy(skin.id)) return;
    this.onEquip(skin);
    this.buildGrid();
    this.show(skin);
  };

  /** Renders every duck once into a small image for its card. */
  private makeThumbs(): void {
    const renderer = this.renderer;
    if (!renderer) return;
    renderer.setSize(THUMB, THUMB, false);
    this.camera.aspect = 1;
    this.camera.updateProjectionMatrix();
    for (const skin of SKINS) {
      const model = buildDuckModel(skin);
      model.rotation.y = 0.5;
      this.scene.add(model);
      renderer.render(this.scene, this.camera);
      this.thumbs.set(skin.id, this.canvas.toDataURL());
      this.scene.remove(model);
      disposeObject3D(model);
    }
  }

  private buildGrid(): void {
    this.coinsLabel.textContent = String(this.profile.coins);
    this.grid.replaceChildren(
      ...SKINS.map((skin) => {
        const card = document.createElement('button');
        card.type = 'button';
        card.className = 'shop-card';
        const owned = this.profile.owned.has(skin.id);
        card.classList.toggle('is-owned', owned);
        card.classList.toggle('is-equipped', skin.id === this.profile.equipped);
        card.classList.toggle('is-selected', skin.id === this.selected.id);
        card.classList.toggle('is-locked', !owned && this.profile.coins < skin.price);
        const img = document.createElement('img');
        img.alt = '';
        img.src = this.thumbs.get(skin.id) ?? '';
        const label = document.createElement('span');
        label.className = 'shop-card-label';
        if (skin.id === this.profile.equipped) label.textContent = '✓';
        else if (owned) label.textContent = skin.name;
        else label.innerHTML = `<i class="coin"></i> ${skin.price}`;
        card.append(img, label);
        card.addEventListener('click', () => {
          this.show(skin);
          this.buildGrid();
        });
        return card;
      }),
    );
  }

  private show(skin: Skin): void {
    this.selected = skinById(skin.id);
    if (this.model) {
      this.scene.remove(this.model);
      disposeObject3D(this.model);
    }
    this.model = buildDuckModel(this.selected);
    this.scene.add(this.model);
    this.nameLabel.textContent = this.selected.name;
    const owned = this.profile.owned.has(this.selected.id);
    const equipped = this.selected.id === this.profile.equipped;
    const short = this.selected.price - this.profile.coins;
    this.action.disabled = equipped || (!owned && short > 0);
    this.action.innerHTML = equipped
      ? 'Racing this duck'
      : owned
        ? 'Race this duck'
        : short > 0
          ? `<i class="coin"></i> ${this.selected.price} · need ${short} more`
          : `Buy for <i class="coin"></i> ${this.selected.price}`;
  }

  private draw(time: number): void {
    const renderer = this.renderer;
    if (!renderer || !this.model) return;
    const width = this.canvas.clientWidth;
    const height = this.canvas.clientHeight;
    if (width === 0 || height === 0) return;
    renderer.setSize(width, height, false);
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
    this.model.rotation.y = time * 0.8;
    this.model.position.y = Math.sin(time * 2.2) * 0.03;
    renderer.render(this.scene, this.camera);
  }

  private get(selector: string): HTMLElement {
    const element = document.querySelector<HTMLElement>(selector);
    if (!element) throw new Error(`Missing shop element: ${selector}`);
    return element;
  }
}
